import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Terminal as XTerm } from 'xterm'
import { FitAddon } from 'xterm-addon-fit'
import { PlayIcon, StopIcon, ArrowPathIcon, ChevronDownIcon, DocumentDuplicateIcon, CheckIcon, NoSymbolIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import { useScanSocket } from '../hooks/useSocket'
import { useAppStore, isScanRunning, type TerminalAuthType } from '../store/appStore'
import { validateFlagsString, wafPreset } from '../utils/sqlmapFlags'
import toast from 'react-hot-toast'
import 'xterm/css/xterm.css'
import { apiFetch, getUserScanSettings, listUserProfiles } from '../utils/api'
import type { UserProfile } from '../utils/api'
import { shallow } from 'zustand/shallow'

const SCAN_PROFILES = [
  { value: 'basic', label: 'Basic Scan', description: 'Quick vulnerability detection' },
  { value: 'deep', label: 'Deep Scan', description: 'Comprehensive analysis' },
  { value: 'enumeration', label: 'Database Enumeration', description: 'Extract database structure' },
  { value: 'dump', label: 'Data Extraction', description: 'Dump vulnerable data' },
  { value: 'custom', label: 'Custom Scan', description: 'Advanced user options' },
]

type BaseFlagMeta = {
  flag: string
  category: string
  description: string
  caution?: string
}

type BaseFlagProfile = {
  id: string
  name: string
  description: string
  flags: string[]
  overlaps: string[]
  additionalFlags: string[]
}

type BaseFlagResponse = {
  baseFlags: BaseFlagMeta[]
  joined: string
  total: number
  profiles: BaseFlagProfile[]
}

export default function Terminal() {
  const terminalRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const clearTerminalOutput = useAppStore(s => s.clearTerminalOutput)
  const xtermRef = useRef<XTerm | null>(null)
  const fitAddonRef = useRef<FitAddon | null>(null)

  const terminalForm = useAppStore(s => s.terminalForm, shallow)
  const updateTerminalForm = useAppStore(s => s.updateTerminalForm)
  const lastScanContext = useAppStore(s => s.lastScanContext)
  const setLastScanContext = useAppStore(s => s.setLastScanContext)
  const selectedProfile = useAppStore(s => s.terminalSelectedProfile)
  const customFlags = useAppStore(s => s.terminalCustomFlags)
  const setTerminalProfile = useAppStore(s => s.setTerminalProfile)
  const setTerminalCustomFlags = useAppStore(s => s.setTerminalCustomFlags)
  const [isScanning, setIsScanning] = useState(false)
  const {
    targetUrl,
    authType,
    authCookie,
    authHeaderName,
    authHeaderValue,
    loginUrl,
    loginUsername,
    loginPassword,
    loginUsernameField,
    loginPasswordField,
    csrfRegex,
    csrfFieldName,
    csrfHeaderName,
    skipQueue,
  } = terminalForm
  // Scheduling UI
  const [scheduleAt, setScheduleAt] = useState<string>('')
  const [jobs, setJobs] = useState<any[]>([])
  const [loadingJobs, setLoadingJobs] = useState(false)

  const isAuthenticated = useAppStore(s => s.isAuthenticated)
  const currentUser = useAppStore(s => s.currentUser)
  const scanPolicyState = useAppStore(s => s.scanPolicy)
  const [baseFlagPanelOpen, setBaseFlagPanelOpen] = useState(false)
  const [baseFlagData, setBaseFlagData] = useState<BaseFlagResponse | null>(null)
  const [baseFlagLoading, setBaseFlagLoading] = useState(false)
  const [baseFlagError, setBaseFlagError] = useState<string | null>(null)
  const [baseFlagCopyState, setBaseFlagCopyState] = useState<'idle' | 'copied' | 'error'>('idle')
  const [userProfiles, setUserProfiles] = useState<UserProfile[]>([])
  const [selectedUserProfile, setSelectedUserProfile] = useState<UserProfile | null>(null)
  const isAdmin = currentUser?.role === 'admin'
  const killSwitchActive = Boolean(!isAdmin && scanPolicyState?.disableNonAdminScans)
  const preapprovedOnly = Boolean(!isAdmin && scanPolicyState?.enforcePreapprovedTargets)
  const terminalPolicyNotices: Array<{ key: string; title: string; body: string; tone: 'warning' | 'danger'; Icon: typeof NoSymbolIcon }> = []
  if (killSwitchActive) {
    terminalPolicyNotices.push({
      key: 'kill-switch',
      title: 'New scans are currently paused for researchers',
      body: 'An administrator temporarily limited SQLMap execution to internal operators. Existing scans keep running, but new requests will be rejected until access is restored.',
      tone: 'danger',
      Icon: NoSymbolIcon
    })
  }
  if (preapprovedOnly) {
    terminalPolicyNotices.push({
      key: 'preapproved',
      title: 'Preview mode locked to curated hosts',
      body: 'While this hold is active you can only scan the safe targets listed on the Targets page. Pick from the curated list or request approval for additional domains.',
      tone: 'warning',
      Icon: ShieldCheckIcon
    })
  }

  const { on, off, startScan: sendStartScan, terminateScan, restartScan: emitRestart } = useScanSocket()
  const addScan = useAppStore(s => s.addScan)
  const updateScan = useAppStore(s => s.updateScan)
  const isConnected = useAppStore(s => s.isConnected)
  const upsertScanFromEvent = useAppStore(s => s.upsertScanFromEvent)
  const addTerminalOutput = useAppStore(s => s.addTerminalOutput)
  const terminalOutput = useAppStore(s => s.terminalOutput)
  const scans = useAppStore(s => s.scans)
  const activeTerminalSession = useAppStore(s => s.activeTerminalSession)

  // Initialize terminal
  const lastStartedTargetRef = useRef<string>('')
  const lastStartedOptionsRef = useRef<any>({})
  const lastStartedProfileRef = useRef<string>('basic')
  const lastScanIdRef = useRef<string>('')
  const pendingScanRef = useRef(false)
  const renderedLengthRef = useRef(0)

  useEffect(() => {
    const container = terminalRef.current
    if (!container) return

    let term = xtermRef.current
    let fitAddon = fitAddonRef.current

    if (!term) {
      term = new XTerm({
        cursorBlink: true,
        convertEol: true,
        fontFamily: `'Fira Code', monospace`,
        fontSize: 14,
        theme: {
          background: '#1f2937', // gray-800
          foreground: '#d1d5db', // gray-300
          cursor: '#60a5fa', // blue-400
        },
      })
      fitAddon = new FitAddon()
      term.loadAddon(fitAddon)
      xtermRef.current = term
      fitAddonRef.current = fitAddon
    } else if (!fitAddon) {
      fitAddon = new FitAddon()
      term.loadAddon(fitAddon)
      fitAddonRef.current = fitAddon
    }

    // Replace any previous terminal nodes without touching raw HTML for safety
    container.replaceChildren()
    term.open(container)
    term.clear()
    renderedLengthRef.current = 0

    setTimeout(() => {
      fitAddonRef.current?.fit()
    }, 1)

    if (!useAppStore.getState().terminalOutput) {
      addTerminalOutput('Welcome to the CyberSec SQLMap Terminal!\n')
      addTerminalOutput('Use the form above to start a scan.\n')
    }

    const handleResize = () => {
      fitAddonRef.current?.fit()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [addTerminalOutput])

  useEffect(() => {
    const activeScan = activeTerminalSession
      ? scans.find((scan) => scan.id === activeTerminalSession && isScanRunning(scan.status))
      : null
    const fallbackScan = activeScan || scans.find((scan) => isScanRunning(scan.status)) || null

    if (fallbackScan) {
      pendingScanRef.current = false
      lastScanIdRef.current = fallbackScan.id
      lastStartedTargetRef.current = fallbackScan.target || ''
      lastStartedProfileRef.current = fallbackScan.scanProfile || 'basic'
      lastStartedOptionsRef.current = fallbackScan.options || {}

      if (!targetUrl && fallbackScan.target) {
        updateTerminalForm({ targetUrl: fallbackScan.target })
      }

      if (!isScanning) {
        setIsScanning(true)
      }
    } else if (!pendingScanRef.current && isScanning) {
      setIsScanning(false)
    }
  }, [scans, activeTerminalSession, isScanning, targetUrl])

  // Load user profiles on mount
  useEffect(() => {
    (async () => {
      try {
        const profiles = await listUserProfiles()
        setUserProfiles(profiles)
      } catch (e) {
        console.warn('Failed to load user profiles:', e)
      }
    })()
  }, [])

  useEffect(() => {
    const term = xtermRef.current
    if (!term) return

    if (terminalOutput.length < renderedLengthRef.current) {
      term.clear()
      renderedLengthRef.current = 0
    }

    const nextChunk = terminalOutput.slice(renderedLengthRef.current)
    if (nextChunk) {
      term.write(nextChunk.replace(/\n/g, '\r\n'))
      renderedLengthRef.current = terminalOutput.length
    }
  }, [terminalOutput])

  // On mount, preload user's last used or default profile
  useEffect(() => {
    (async () => {
      try {
        const settings = await getUserScanSettings().catch(() => null)
        if (settings) {
          const next = settings.last_used_profile || settings.default_profile || 'basic'
          if (next && next !== selectedProfile) {
            setTerminalProfile(next)
          }
        }
      } catch (_) { }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isAuthenticated) return
    let active = true

    const loadBaseFlags = async () => {
      try {
        setBaseFlagLoading(true)
        const payload = await apiFetch<BaseFlagResponse>('/api/sqlmap/base-flags')
        if (active) {
          setBaseFlagData(payload)
          setBaseFlagError(null)
        }
      } catch (err: any) {
        if (active) {
          setBaseFlagError(err?.message || 'Failed to load base flags')
        }
      } finally {
        if (active) {
          setBaseFlagLoading(false)
        }
      }
    }

    loadBaseFlags()

    return () => {
      active = false
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (baseFlagCopyState === 'idle') return
    const timeout = setTimeout(() => setBaseFlagCopyState('idle'), baseFlagCopyState === 'copied' ? 2000 : 3000)
    return () => clearTimeout(timeout)
  }, [baseFlagCopyState])

  useEffect(() => {
    if (location.hash === '#base-flags') {
      setBaseFlagPanelOpen(true)
    }
  }, [location.hash])

  useEffect(() => {
    if (!lastScanContext) return
    if (lastScanContext.target) {
      lastStartedTargetRef.current = lastScanContext.target
    }
    if (lastScanContext.profile) {
      lastStartedProfileRef.current = lastScanContext.profile
    }
    if (lastScanContext.options) {
      lastStartedOptionsRef.current = { ...lastScanContext.options }
    }
    if (lastScanContext.scanId) {
      lastScanIdRef.current = lastScanContext.scanId
    }
  }, [lastScanContext])

  useEffect(() => {
    const shouldBeScanning = lastScanContext?.status === 'running' || lastScanContext?.status === 'pending'
    if (!pendingScanRef.current) {
      setIsScanning(Boolean(shouldBeScanning))
    }
  }, [lastScanContext?.status])

  // Socket event handlers
  useEffect(() => {
    if (!on) return

    const handleScanStarted = (data: { scanId: string; target?: string; sessionId?: string; startTime?: string }) => {
      const wasPending = pendingScanRef.current
      lastScanIdRef.current = data.scanId
      if (data.target) {
        lastStartedTargetRef.current = data.target
      }
      pendingScanRef.current = false
      if (!wasPending) {
        const bannerTarget = data.target || lastStartedTargetRef.current || data.scanId
        addTerminalOutput('\n')
        addTerminalOutput(`\x1b[1;36m→ Scan started for: ${bannerTarget}\x1b[0m\n`)
        addTerminalOutput('\n')
      }
      setIsScanning(true)
      const startedAt = data.startTime || new Date().toISOString()
      addScan({
        id: data.scanId,
        target: lastStartedTargetRef.current,
        options: lastStartedOptionsRef.current,
        scanProfile: lastStartedProfileRef.current,
        status: 'running',
        startTime: startedAt,
        output: '',
        createdAt: startedAt,
        updatedAt: startedAt,
      } as any)

      const previousContext = useAppStore.getState().lastScanContext
      setLastScanContext({
        scanId: data.scanId,
        target: lastStartedTargetRef.current || data.target || previousContext?.target || '',
        profile: lastStartedProfileRef.current,
        options: lastStartedOptionsRef.current,
        status: 'running',
        startedAt,
        completedAt: null,
        exitCode: null,
        sessionId: data.sessionId ?? previousContext?.sessionId ?? null,
        error: null,
      })
    }

    const handleScanComplete = (data: { scanId: string; status?: string; reportId?: string; exit_code?: number }) => {
      pendingScanRef.current = false
      setIsScanning(false)

      addTerminalOutput('\n')
      if (data.status === 'failed') {
        addTerminalOutput('\x1b[1;31m✗ Scan finished with a failure status. Review the report or logs for details.\x1b[0m\n')
      } else {
        addTerminalOutput('\x1b[1;32m✓ Scan completed successfully!\x1b[0m\n')
      }
      if (data.reportId) {
        addTerminalOutput(`\x1b[36mReport ID: ${data.reportId}\x1b[0m\n`)
      }
      addTerminalOutput('\n')

      if (data.scanId) {
        updateScan(data.scanId, { status: (data.status as any) || 'completed' })
      }

      const exitCode = typeof data.exit_code === 'number' ? data.exit_code : null
      const completedAt = new Date().toISOString()
      setLastScanContext({
        scanId: data.scanId,
        status: data.status === 'failed' ? 'failed' : 'completed',
        completedAt,
        exitCode,
        error: data.status === 'failed' ? useAppStore.getState().lastScanContext?.error ?? null : null,
      })
    }

    const handleScanError = (data: { scanId?: string; message?: string; error?: string }) => {
      pendingScanRef.current = false
      setIsScanning(false)

      const msg = (data?.message || data?.error || 'Unknown error').toString()
      addTerminalOutput('\n')
      addTerminalOutput(`\x1b[1;31m✗ Scan failed: ${msg}\x1b[0m\n`)
      addTerminalOutput('\n')

      if (data?.scanId) {
        updateScan(data.scanId, { status: 'failed', error: msg })
      }

      const completedAt = new Date().toISOString()
      setLastScanContext({
        scanId: data?.scanId ?? useAppStore.getState().lastScanContext?.scanId ?? null,
        status: 'failed',
        error: msg,
        completedAt,
      })

      const lower = msg.toLowerCase()
      if (lower.includes('similar scan was just started')) {
        toast('Duplicate prevented: an identical scan is already running.', { icon: 'ℹ️' })
      } else if (lower.includes('another scan is being started')) {
        toast('Please wait a moment, your previous start request is still processing.', { icon: '⏳' })
      } else if (lower.includes('concurrent scan limit reached')) {
        toast.error(msg)
      } else {
        toast.error(`Scan failed: ${msg}`)
      }
    }

    const handleScanTerminated = (data?: { scanId?: string }) => {
      pendingScanRef.current = false
      setIsScanning(false)
      addTerminalOutput('\n')
      addTerminalOutput('\x1b[1;33m! Scan was terminated by user or server\x1b[0m\n')
      addTerminalOutput('\n')
      toast('Scan terminated', { icon: '🛑' })

      setLastScanContext({
        scanId: data?.scanId ?? useAppStore.getState().lastScanContext?.scanId ?? null,
        status: 'terminated',
        completedAt: new Date().toISOString(),
      })
    }

    const handleScanStillRunning = (running: Array<{ scanId: string; target: string; scanProfile?: string; startTime?: string }>) => {
      try {
        if (Array.isArray(running) && running.length > 0) {
          running.forEach((r) => {
            upsertScanFromEvent({
              id: r.scanId,
              target: r.target,
              scanProfile: r.scanProfile || 'basic',
              startTime: r.startTime || new Date().toISOString(),
              status: 'running',
            } as any)
          })
          pendingScanRef.current = false
          setIsScanning(true)
          const count = running.length
          addTerminalOutput('\n')
          addTerminalOutput(`\x1b[1;36m↻ Restored ${count} running scan${count > 1 ? 's' : ''} after reconnect\x1b[0m\n`)
          addTerminalOutput('\n')
          toast(`Restored ${count} running scan${count > 1 ? 's' : ''}`, { icon: '🔄' })

          const first = running[0]
          if (first) {
            setLastScanContext({
              scanId: first.scanId,
              target: first.target,
              profile: first.scanProfile || lastStartedProfileRef.current,
              options: useAppStore.getState().lastScanContext?.options ?? lastStartedOptionsRef.current,
              status: 'running',
              startedAt: first.startTime || useAppStore.getState().lastScanContext?.startedAt || null,
              completedAt: null,
              exitCode: null,
              error: null,
            })
          }
        }
      } catch (e) {
        console.warn('Failed to handle scan-still-running:', e)
      }
    }

    on('scan-started', handleScanStarted)
    on('scan-completed', handleScanComplete)
    on('scan-error', handleScanError)
    on('scan-terminated', handleScanTerminated)
    on('scan-still-running', handleScanStillRunning)

    return () => {
      off('scan-started', handleScanStarted)
      off('scan-completed', handleScanComplete)
      off('scan-error', handleScanError)
      off('scan-terminated', handleScanTerminated)
      off('scan-still-running', handleScanStillRunning)
    }
  }, [on, off, updateScan, addScan, upsertScanFromEvent, setLastScanContext, addTerminalOutput])

  const startScan = async () => {
    const sanitizedTarget = targetUrl.trim()

    if (!sanitizedTarget) {
      toast.error('Please enter a target URL')
      return
    }

    const currentContext = useAppStore.getState().lastScanContext
    if (currentContext?.status === 'running') {
      toast.error('A scan is already running. Stop it or wait for it to finish before starting another.')
      return
    }

    if (!isConnected) {
      toast.error('Not connected to server')
      return
    }

    const scanOptions: any = {
      target: sanitizedTarget,
      profile: selectedProfile,
      ...(selectedProfile === 'custom' && customFlags.trim() && {
        customFlags: customFlags.trim()
      })
    }

    // Add flag toggles if a user profile is selected
    if (selectedUserProfile && selectedUserProfile.flagToggles) {
      scanOptions.flagToggles = selectedUserProfile.flagToggles
    }

    // Attach auth block
    if (authType === 'cookie') {
      const headers: Record<string, string> = {}
      if (authHeaderName && authHeaderValue) headers[authHeaderName] = authHeaderValue
      scanOptions.auth = { type: 'cookie', cookie: authCookie || undefined, headers }
      // Also pass direct fields for convenience
      if (authCookie) scanOptions.cookie = authCookie
      if (authHeaderName && authHeaderValue) scanOptions.headers = headers
    } else if (authType === 'login') {
      scanOptions.auth = {
        type: 'login',
        loginUrl,
        method: 'POST',
        username: loginUsername,
        password: loginPassword,
        usernameField: loginUsernameField || 'username',
        passwordField: loginPasswordField || 'password',
        csrf: {
          regex: csrfRegex || undefined,
          fieldName: csrfFieldName || undefined,
          headerName: csrfHeaderName || undefined,
          tokenUrl: loginUrl || undefined
        }
      }
    }

    try {
      pendingScanRef.current = true
      setIsScanning(true)
      lastStartedTargetRef.current = sanitizedTarget
      lastStartedProfileRef.current = selectedProfile
      lastStartedOptionsRef.current = scanOptions
      lastScanIdRef.current = ''
      const timestamp = new Date().toISOString()
      setLastScanContext({
        scanId: null,
        target: sanitizedTarget,
        profile: selectedProfile,
        options: scanOptions,
        status: 'pending',
        startedAt: timestamp,
        completedAt: null,
        exitCode: null,
        sessionId: null,
        error: null,
      })
      addTerminalOutput('\n')
      addTerminalOutput(`\x1b[1;36m→ Starting ${selectedProfile} scan for: ${sanitizedTarget}\x1b[0m\n`)
      addTerminalOutput('\n')

      // Emit scan start event via socket
      sendStartScan(sanitizedTarget, scanOptions, selectedProfile, skipQueue)

    } catch (error) {
      setIsScanning(false)
      pendingScanRef.current = false
      setLastScanContext({ status: 'idle', error: error instanceof Error ? error.message : 'Failed to start scan' })
      toast.error('Failed to start scan')
      console.error('Scan start error:', error)
    }
  }

  const scheduleScan = async () => {
    const sanitizedTarget = targetUrl.trim()
    if (!sanitizedTarget) { toast.error('Please enter a target URL'); return }
    try {
      const payload: any = { target: sanitizedTarget, scanProfile: selectedProfile }
      // Reuse auth block from startScan
      if (authType === 'cookie') {
        const headers: Record<string, string> = {}
        if (authHeaderName && authHeaderValue) headers[authHeaderName] = authHeaderValue
        payload.options = { auth: { type: 'cookie', cookie: authCookie || undefined, headers }, cookie: authCookie || undefined, headers: headers }
      } else if (authType === 'login') {
        payload.options = { auth: { type: 'login', loginUrl, method: 'POST', username: loginUsername, password: loginPassword, usernameField: loginUsernameField || 'username', passwordField: loginPasswordField || 'password', csrf: { regex: csrfRegex || undefined, fieldName: csrfFieldName || undefined, headerName: csrfHeaderName || undefined, tokenUrl: loginUrl || undefined } } }
      }
      if (scheduleAt) payload.runAt = new Date(scheduleAt).toISOString()
      const res = await apiFetch<{ jobId: string, status: string, runAt: string }>(`/api/scans/schedule`, { method: 'POST', body: JSON.stringify(payload) })
      toast.success(`Scheduled (job ${res.jobId}) for ${new Date(res.runAt).toLocaleString()}`)
      setScheduleAt('')
      await refreshJobs()
    } catch (e: any) {
      toast.error(e.message || 'Failed to schedule scan')
    }
  }

  const refreshJobs = async () => {
    try {
      setLoadingJobs(true)
      const data = await apiFetch<any[]>(`/api/queue`)
      setJobs(data)
    } catch (e: any) {
      toast.error(e.message || 'Failed to load queue')
    } finally {
      setLoadingJobs(false)
    }
  }

  const cancelJob = async (id: string) => {
    try {
      await apiFetch(`/api/jobs/${encodeURIComponent(id)}`, { method: 'DELETE' })
      toast('Job canceled', { icon: '🗑️' })
      await refreshJobs()
    } catch (e: any) {
      toast.error(e.message || 'Failed to cancel job')
    }
  }

  const stopScan = () => {
    if (isScanning) {
      terminateScan(lastScanIdRef.current)
      toast('Scan termination signal sent.', {
        icon: '🛑',
      })
      setIsScanning(false)
      pendingScanRef.current = false
    } else {
      toast.error('No active scan to stop.')
    }
  }

  const restartScan = () => {
    const storedContext = useAppStore.getState().lastScanContext
    const lastTarget = (storedContext?.target || lastStartedTargetRef.current || '').trim()
    const lastProfile = storedContext?.profile || lastStartedProfileRef.current
    const lastOptions = storedContext?.options && Object.keys(storedContext.options).length > 0
      ? storedContext.options
      : lastStartedOptionsRef.current

    if (!lastTarget) {
      toast.error('No previous scan parameters to restart')
      return
    }

    // If a scan is currently running, send terminate first
    if (isScanning) {
      terminateScan(lastScanIdRef.current)
      addTerminalOutput('\n')
      addTerminalOutput('\x1b[1;33m! Terminating current scan before restart...\x1b[0m\n')
      // Small delay to allow server to process termination
      setTimeout(() => {
        pendingScanRef.current = true
        setIsScanning(true)
        const startedAt = new Date().toISOString()
        setLastScanContext({
          target: lastTarget,
          profile: lastProfile,
          options: lastOptions,
          status: 'pending',
          startedAt,
          completedAt: null,
          exitCode: null,
          error: null,
        })
        addTerminalOutput('\n')
        addTerminalOutput(`\x1b[1;36m↻ Restarting ${lastProfile} scan for: ${lastTarget}\x1b[0m\n`)
        addTerminalOutput('\n')
        sendStartScan(lastTarget, lastOptions, lastProfile)
      }, 600)
      return
    }

    // No active scan; just start again with last params
    pendingScanRef.current = true
    setIsScanning(true)
    const startedAt = new Date().toISOString()
    setLastScanContext({
      target: lastTarget,
      profile: lastProfile,
      options: lastOptions,
      status: 'pending',
      startedAt,
      completedAt: null,
      exitCode: null,
      error: null,
    })
    addTerminalOutput('\n')
    addTerminalOutput(`\x1b[1;36m↻ Restarting ${lastProfile} scan for: ${lastTarget}\x1b[0m\n`)
    addTerminalOutput('\n')
    // Prefer server-side restart endpoint if we had a prior scanId
    if (lastScanIdRef.current) {
      emitRestart(lastScanIdRef.current, { target: lastTarget, options: lastOptions, scanProfile: lastProfile })
    } else {
      sendStartScan(lastTarget, lastOptions, lastProfile)
    }
  }

  const handleCopyBaseFlags = async () => {
    if (!baseFlagData?.joined) return
    try {
      if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
        throw new Error('Clipboard API unavailable')
      }
      await navigator.clipboard.writeText(baseFlagData.joined)
      setBaseFlagCopyState('copied')
      toast.success('Base scan defaults copied to clipboard')
    } catch (err) {
      setBaseFlagCopyState('error')
      toast.error('Unable to copy the base scan defaults')
    }
  }

  const clearTerminal = () => {
    if (xtermRef.current) {
      xtermRef.current.clear()
    }
    clearTerminalOutput()
    renderedLengthRef.current = 0
    addTerminalOutput('\x1b[90m[terminal cleared]\x1b[0m\n')
  }

  const startButtonDisabled = killSwitchActive || !isConnected || !targetUrl.trim()
  const startButtonTitle = killSwitchActive ? 'Scanning is temporarily limited to administrators' : undefined
  const restartButtonDisabled = killSwitchActive || !isConnected || !lastStartedTargetRef.current
  const restartButtonTitle = killSwitchActive
    ? 'Scanning is temporarily limited to administrators'
    : (lastStartedTargetRef.current
      ? `Restart last scan for ${lastStartedTargetRef.current}`
      : 'Start a scan first to enable restart')
  const scheduleDisabled = killSwitchActive || !isConnected
  const scheduleTitle = killSwitchActive ? 'Scheduling is unavailable while scanning is paused' : undefined

  return (
    <div className="h-full flex flex-col bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-6 sm:px-6 lg:px-10">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <PlayIcon className="h-6 w-6 text-green-400" />
              SQLMap Terminal
            </h1>
            <p className="text-sm text-gray-400 text-balance">
              Interactive terminal for SQL injection testing
            </p>
            {terminalPolicyNotices.length > 0 && (
              <div className="mt-4 space-y-3">
                {terminalPolicyNotices.map(({ key, title, body, tone, Icon }) => (
                  <div
                    key={key}
                    className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${tone === 'danger' ? 'border-red-500/60 bg-red-900/20' : 'border-amber-400/60 bg-amber-900/20'
                      }`}
                  >
                    <Icon
                      className={`h-5 w-5 mt-0.5 ${tone === 'danger' ? 'text-red-200' : 'text-amber-200'}`}
                      aria-hidden="true"
                    />
                    <div>
                      <p className="text-sm font-semibold text-white">{title}</p>
                      <p className="text-xs text-gray-200 mt-1">{body}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2" htmlFor="terminal-target-url">
              Target URL
            </label>
            <input
              id="terminal-target-url"
              type="url"
              value={targetUrl}
              onChange={(e) => updateTerminalForm({ targetUrl: e.target.value })}
              placeholder="https://example.com/vulnerable.php?id=1"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isScanning}
            />
            {preapprovedOnly && (
              <p className="mt-2 text-xs text-amber-300">
                Preview users are limited to the curated safe hosts listed on the Targets page while this hold is active.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="scanProfile" className="block text-sm font-medium text-gray-300 mb-2">
              Scan Profile
            </label>
            <select
              id="scanProfile"
              aria-label="Scan Profile"
              value={selectedProfile}
              onChange={(e) => {
                const profileValue = e.target.value
                setTerminalProfile(profileValue)

                // If a user profile is selected, set the custom flags from that profile
                if (profileValue.startsWith('profile-')) {
                  const userProfile = userProfiles.find(p => `profile-${p.id}` === profileValue)
                  if (userProfile) {
                    setSelectedUserProfile(userProfile)
                    // Set custom flags from the user profile
                    const flagsStr = userProfile.flags.join(' ')
                    setTerminalCustomFlags(flagsStr)
                  }
                } else {
                  setSelectedUserProfile(null)
                }
              }}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isScanning}
            >
              <optgroup label="Built-in Profiles">
                {SCAN_PROFILES.map((profile) => (
                  <option key={profile.value} value={profile.value}>
                    {profile.label}
                  </option>
                ))}
              </optgroup>
              {userProfiles.length > 0 && (
                <optgroup label="Custom Profiles">
                  {userProfiles.map((profile) => (
                    <option key={profile.id} value={`profile-${profile.id}`}>
                      {profile.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
        </div>

        {/* Quick Scan Toggle */}
        <div className="mt-3 flex items-center gap-2">
          <input
            type="checkbox"
            id="skipQueue"
            checked={skipQueue}
            onChange={(e) => updateTerminalForm({ skipQueue: e.target.checked })}
            disabled={isScanning}
            className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0"
          />
          <label htmlFor="skipQueue" className="text-sm text-gray-300 cursor-pointer select-none">
            Quick Scan (skip pre-scan checks)
          </label>
        </div>

        <div className="mt-4 flex flex-wrap items-start gap-2">
          {!isScanning ? (
            <button
              onClick={startScan}
              disabled={startButtonDisabled}
              title={startButtonTitle}
              className="btn-success flex w-full items-center justify-center gap-2 sm:w-auto"
            >
              <PlayIcon className="h-4 w-4" />
              Start Scan
            </button>
          ) : (
            <button
              onClick={stopScan}
              className="btn-danger flex w-full items-center justify-center gap-2 sm:w-auto"
            >
              <StopIcon className="h-4 w-4" />
              Stop Scan
            </button>
          )}
          <button
            onClick={restartScan}
            className="btn-primary flex w-full items-center justify-center gap-2 sm:w-auto"
            disabled={restartButtonDisabled}
            title={restartButtonTitle}
          >
            <ArrowPathIcon className="h-4 w-4" />
            Restart
          </button>

          <button
            onClick={clearTerminal}
            className="btn-secondary flex w-full items-center justify-center gap-2 sm:w-auto"
            disabled={isScanning}
          >
            <ArrowPathIcon className="h-4 w-4" />
            Clear
          </button>

          <div className="flex flex-col gap-2 sm:ml-auto sm:flex-row sm:items-center sm:gap-2 w-full sm:w-auto">
            <label htmlFor="scheduleAt" className="sr-only">Schedule At</label>
            <input
              id="scheduleAt"
              type="datetime-local"
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
              className="w-full sm:w-56 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Schedule At"
            />
            <button
              onClick={scheduleScan}
              className="btn-primary flex w-full items-center justify-center gap-2 sm:w-auto"
              disabled={scheduleDisabled}
              title={scheduleTitle}
            >
              Schedule
            </button>
            <button onClick={refreshJobs} className="btn-secondary flex w-full items-center justify-center gap-2 sm:w-auto">Queue</button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-10 space-y-6">
        {/* Auth Section */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div>
            <label htmlFor="authMode" className="block text-sm font-medium text-gray-300 mb-2">Auth Mode</label>
            <select
              id="authMode"
              value={authType}
              onChange={(e) => updateTerminalForm({ authType: e.target.value as TerminalAuthType })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isScanning}
            >
              <option value="none">None</option>
              <option value="cookie">Cookie/Header</option>
              <option value="login">Login Flow</option>
            </select>
          </div>
          {authType === 'cookie' && (
            <>
              <div className="lg:col-span-1">
                <label className="block text-sm font-medium text-gray-300 mb-2">Cookie</label>
                <input type="text" value={authCookie} onChange={(e) => updateTerminalForm({ authCookie: e.target.value })} placeholder="sessionid=abc; other=..."
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Header Name</label>
                <input type="text" value={authHeaderName} onChange={(e) => updateTerminalForm({ authHeaderName: e.target.value })} placeholder="X-Auth-Token"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Header Value</label>
                <input type="text" value={authHeaderValue} onChange={(e) => updateTerminalForm({ authHeaderValue: e.target.value })} placeholder="..."
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            </>
          )}
          {authType === 'login' && (
            <>
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Login URL</label>
                <input type="url" value={loginUrl} onChange={(e) => updateTerminalForm({ loginUrl: e.target.value })} placeholder="https://example.com/login"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label htmlFor="loginUsername" className="block text-sm font-medium text-gray-300 mb-2">Username</label>
                <input id="loginUsername" type="text" value={loginUsername} onChange={(e) => updateTerminalForm({ loginUsername: e.target.value })} placeholder="username"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label htmlFor="loginPassword" className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                <input id="loginPassword" type="password" value={loginPassword} onChange={(e) => updateTerminalForm({ loginPassword: e.target.value })} placeholder="password"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Username Field</label>
                <input type="text" value={loginUsernameField} onChange={(e) => updateTerminalForm({ loginUsernameField: e.target.value })} placeholder="username"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Password Field</label>
                <input type="text" value={loginPasswordField} onChange={(e) => updateTerminalForm({ loginPasswordField: e.target.value })} placeholder="password"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">CSRF Regex (group 1)</label>
                <input type="text" value={csrfRegex} onChange={(e) => updateTerminalForm({ csrfRegex: e.target.value })} placeholder='name="_token" value="([^"]+)"'
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">CSRF Field Name</label>
                <input type="text" value={csrfFieldName} onChange={(e) => updateTerminalForm({ csrfFieldName: e.target.value })} placeholder="_token"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">CSRF Header Name (optional)</label>
                <input type="text" value={csrfHeaderName} onChange={(e) => updateTerminalForm({ csrfHeaderName: e.target.value })} placeholder="X-CSRF-Token"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            </>
          )}
        </div>

        {/* Queue Panel */}
        <div className="mt-4 px-4 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-white font-semibold">Queue</h2>
            <button onClick={refreshJobs} className="text-sm px-2 py-1 bg-gray-700 text-white rounded">Refresh</button>
          </div>
          {loadingJobs ? (
            <p className="text-gray-400 mt-2">Loading…</p>
          ) : (
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-sm text-gray-300">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-400">
                    <th className="py-2 pr-3">Job</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Error</th>
                    <th className="py-2 pr-3">Run At</th>
                    <th className="py-2 pr-3">Retries</th>
                    <th className="py-2 pr-3">Target</th>
                    <th className="py-2 pr-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map(j => (
                    <tr key={j.id} className="border-t border-gray-700">
                      <td className="py-2 pr-3 font-mono text-xs break-all text-gray-200">{j.id}</td>
                      <td className="py-2 pr-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span>{j.status}</span>
                          {j.created_by_admin ? <span className="text-[10px] px-1 py-0.5 bg-blue-800 text-blue-100 rounded">admin</span> : null}
                        </div>
                      </td>
                      <td className="py-2 pr-3 max-w-[18rem] break-words text-xs text-gray-400" title={j.last_error || ''}>{j.last_error || ''}</td>
                      <td className="py-2 pr-3 text-xs text-gray-400 leading-snug">{new Date(j.run_at).toLocaleString()}</td>
                      <td className="py-2 pr-3 whitespace-nowrap text-xs text-gray-300">{j.retries}/{j.max_retries}</td>
                      <td className="py-2 pr-3 break-all text-xs text-gray-200">{j.target}</td>
                      <td className="py-2 pl-3 pr-0 text-right">
                        {(j.status === 'scheduled' || j.status === 'retrying') && (
                          <button onClick={() => cancelJob(j.id)} className="px-2 py-1 bg-red-700 text-white rounded">Cancel</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {jobs.length === 0 && (
                    <tr><td colSpan={7} className="py-3 text-gray-500">No queued jobs</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {isAuthenticated && (
          <div className="mt-4 px-4 sm:px-6">
            <div className="overflow-hidden rounded-lg border border-gray-700 bg-gray-800">
              <button
                type="button"
                onClick={() => setBaseFlagPanelOpen((prev) => !prev)}
                className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-gray-200 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900"
              >
                <span>Base scan flags (always applied)</span>
                <ChevronDownIcon
                  className={`h-5 w-5 transition-transform ${baseFlagPanelOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {baseFlagPanelOpen && (
                <div className="border-t border-gray-700 px-4 py-4 text-sm text-gray-300">
                  {baseFlagLoading ? (
                    <p className="text-gray-400">Loading defaults…</p>
                  ) : baseFlagError ? (
                    <p className="text-red-400">{baseFlagError}</p>
                  ) : baseFlagData ? (
                    <>
                      <p className="text-gray-300">
                        These {baseFlagData.total} safeguards are appended to every scan to balance coverage, evidence collection, and safety.
                      </p>
                      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                        <span className="text-xs text-gray-500">
                          Defaults tracked on the server • total defaults: {baseFlagData.total}
                        </span>
                        {baseFlagData.joined && (
                          <button
                            type="button"
                            onClick={handleCopyBaseFlags}
                            className="inline-flex items-center gap-2 rounded-md border border-gray-600 bg-gray-700 px-3 py-1.5 text-xs text-gray-200 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900"
                          >
                            {baseFlagCopyState === 'copied' ? (
                              <>
                                <CheckIcon className="h-4 w-4 text-green-400" />
                                Copied
                              </>
                            ) : baseFlagCopyState === 'error' ? (
                              <>
                                <DocumentDuplicateIcon className="h-4 w-4 text-red-400" />
                                Try again
                              </>
                            ) : (
                              <>
                                <DocumentDuplicateIcon className="h-4 w-4 text-gray-300" />
                                Copy defaults
                              </>
                            )}
                          </button>
                        )}
                      </div>
                      <ul className="mt-4 space-y-3">
                        {baseFlagData.baseFlags.map((flag) => (
                          <li key={flag.flag} className="rounded-lg border border-gray-700 bg-gray-900/70 px-3 py-2">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <code className="break-all text-xs text-blue-300">{flag.flag}</code>
                              <span className="rounded-full bg-gray-900 px-2 py-0.5 text-[10px] uppercase tracking-wide text-gray-400">
                                {flag.category}
                              </span>
                            </div>
                            <p className="mt-2 text-xs text-gray-400">{flag.description}</p>
                            {flag.caution ? (
                              <p className="mt-1 text-xs text-amber-400">{flag.caution}</p>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                      {baseFlagData.profiles?.length ? (
                        <div className="mt-6">
                          <h3 className="text-sm font-semibold text-gray-200">Profile adjustments</h3>
                          <p className="mt-1 text-xs text-gray-400">
                            Each profile inherits the defaults above and may add or override additional flags to match its strategy.
                          </p>
                          <div className="mt-3 space-y-3">
                            {baseFlagData.profiles.map((profile) => (
                              <div key={profile.id} className="rounded-lg border border-gray-700 bg-gray-900/60 p-3">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div>
                                    <div className="text-sm font-medium text-gray-200">{profile.name}</div>
                                    <div className="text-xs text-gray-500">{profile.id}</div>
                                  </div>
                                  <span className="text-[11px] uppercase tracking-wide text-gray-400">{profile.additionalFlags.length} custom flag{profile.additionalFlags.length === 1 ? '' : 's'}</span>
                                </div>
                                {profile.description && (
                                  <p className="mt-1 text-xs text-gray-400">{profile.description}</p>
                                )}
                                {profile.additionalFlags.length > 0 ? (
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    {profile.additionalFlags.map((flag) => (
                                      <span key={flag} className="rounded-full bg-blue-900/40 px-2 py-0.5 text-[11px] text-blue-200 border border-blue-700/40">
                                        {flag}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="mt-2 text-xs text-gray-500">No extras—relies solely on the base defaults.</p>
                                )}
                                {profile.overlaps.length > 0 && (
                                  <div className="mt-2 text-[11px] text-gray-500">
                                    Shares defaults: {profile.overlaps.join(', ')}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <p className="text-gray-400">No base flags are currently configured.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Custom Flags (only for custom profile) */}
        {selectedProfile === 'custom' && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Custom SQLMap Flags
            </label>
            <input
              type="text"
              value={customFlags}
              onChange={(e) => setTerminalCustomFlags(e.target.value)}
              placeholder="--level=3 --risk=2 --tamper=space2comment"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isScanning}
            />
            <p className="mt-1 text-xs text-gray-400">
              Enter additional SQLMap flags for advanced customization
            </p>
            <div className="flex gap-2 items-center">
              <details className="relative">
                <summary className="px-2 py-2 bg-gray-700 border border-gray-600 rounded-md text-white cursor-pointer select-none text-xs">WAF presets</summary>
                <div className="absolute right-0 mt-1 w-56 bg-gray-800 border border-gray-700 rounded shadow-lg z-10">
                  <button type="button" className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-700" onClick={() => { setTerminalProfile('custom'); setTerminalCustomFlags(wafPreset('light')) }}>Light</button>
                  <button type="button" className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-700" onClick={() => { setTerminalProfile('custom'); setTerminalCustomFlags(wafPreset('standard')) }}>Standard</button>
                  <button type="button" className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-700" onClick={() => { setTerminalProfile('custom'); setTerminalCustomFlags(wafPreset('strict')) }}>Strict</button>
                </div>
              </details>
            </div>
            {!validateFlagsString(customFlags).ok && (
              <p className="mt-1 text-xs text-yellow-400">
                Warning: disallowed flags detected: {validateFlagsString(customFlags).disallowed.join(', ')}
              </p>
            )}
          </div>
        )}

        {/* Profile Description */}
        <div className="mt-3">
          {selectedUserProfile ? (
            <div className="p-3 bg-blue-900 border border-blue-700 rounded-md">
              <p className="text-sm text-blue-300">
                <span className="font-medium">Custom Profile:</span> {selectedUserProfile.name}
              </p>
              {selectedUserProfile.description && (
                <p className="text-sm text-blue-200 mt-1">{selectedUserProfile.description}</p>
              )}
              {selectedUserProfile.flags.length > 0 && (
                <div className="text-xs text-blue-100 mt-2 max-h-20 overflow-y-auto font-mono bg-black bg-opacity-30 p-2 rounded">
                  {selectedUserProfile.flags.join(' ')}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400">
              <span className="font-medium">
                {SCAN_PROFILES.find(p => p.value === selectedProfile)?.label}:
              </span>{' '}
              {SCAN_PROFILES.find(p => p.value === selectedProfile)?.description}
            </p>
          )}
        </div>
      </div>

      {/* Terminal */}
      <div className="flex-1 bg-gray-900 px-4 py-4 sm:px-6 lg:px-10">
        <div className="h-full bg-[#0d1117] rounded-lg border border-gray-700 overflow-hidden">
          <div className="h-full w-full overflow-x-auto">
            <div ref={terminalRef} className="h-full min-w-[320px] p-4" />
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div className="bg-gray-800 border-t border-gray-700 px-4 py-2 sm:px-6 lg:px-10">
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center">
              <div className={`w-2 h-2 rounded-full mr-2 ${isConnected ? 'bg-green-400' : 'bg-red-400'
                }`}></div>
              <span className="text-gray-400">
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>

            {isScanning && (
              <div className="flex items-center">
                <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2 animate-pulse"></div>
                <span className="text-yellow-400">Scanning...</span>
              </div>
            )}
          </div>

          <div className="text-gray-400 text-sm">
            Press Ctrl+C to interrupt • Use the controls above to manage scans
          </div>
        </div>
      </div>
    </div>
  )
} 
